import React from 'react'

const TextTemplate: React.FC<any> = ({
  message,
  side,
  readByRecipientId,
  uploadFileUrl,
  apiKey
}) => {
  function getFileNameWithExtension(url: string) {
    if (!url) return ''
    const splitArr = url?.split('-')
    const fileExtension = splitArr[splitArr?.length - 1]?.toLowerCase() || ''
    return fileExtension
  }

  function getFileExtension(url: string) {
    if (!url) return ''
    const splitArr = url?.split('.')
    const fileExtension = splitArr[splitArr?.length - 1]?.toLowerCase() || ''
    return fileExtension
  }

  function isMapUrl(text: string) {
    const mapPatterns = [
      /https?:\/\/(www\.)?google\.com\/maps/i,
      /https?:\/\/maps\.google\.com/i,
      /https?:\/\/(www\.)?openstreetmap\.org/i,
      /https?:\/\/(www\.)?bing\.com\/maps/i,
      /https?:\/\/maps\.app\.goo\.gl/i,
      /https?:\/\/g\.co\/kgs/i
    ]
    return mapPatterns.some((pattern) => pattern.test(text))
  }

  function extractLocationFromUrl(url: string) {
    // Extract location from various URL formats
    let location = ''

    if (url.includes('?q=')) {
      const qIndex = url.indexOf('?q=')
      location = url.slice(qIndex + 3).split('&')[0]
    } else if (url.includes('/place/')) {
      const placeIndex = url.indexOf('/place/')
      location = url.slice(placeIndex + 7).split('/')[0]
    } else if (url.includes('@')) {
      const match = url.match(/@(-?\d+\.\d+),(-?\d+\.\d+)/)
      if (match) {
        location = `${match[1]},${match[2]}`
      }
    }

    return decodeURIComponent(location)
  }

  function getEmbedMapUrl(url: string) {
    if (url.includes('maps.app.goo.gl') || url.includes('g.co/kgs')) {
      return url // Return original URL for shortened links
    }

    const location = extractLocationFromUrl(url)
    if (location) {
      return `https://www.google.com/maps/embed/v1/place?key=${apiKey}&q=${encodeURIComponent(
        location
      )}`
    }

    // Fallback to search with the whole URL
    return `https://www.google.com/maps/embed/v1/search?key=${apiKey}&q=${encodeURIComponent(url)}`
  }

  function displayMap(url: string) {
    // For shortened URLs, display as a clickable link
    if (url.includes('maps.app.goo.gl') || url.includes('g.co/kgs')) {
      return (
        <a
          href={url}
          target='_blank'
          rel='noopener noreferrer'
          className='text-blue-600 hover:text-blue-800 underline flex items-center gap-2'
        >
          {url}
        </a>
      )
    }

    // For regular map URLs, display in iframe if API key is available
    if (apiKey) {
      return (
        <div className='w-full h-64 rounded overflow-hidden'>
          <iframe
            src={getEmbedMapUrl(url)}
            width='100%'
            height='100%'
            style={{ border: 0 }}
            allowFullScreen
            loading='lazy'
            referrerPolicy='no-referrer-when-downgrade'
            className='rounded'
          />
        </div>
      )
    } else {
      // Fallback to link if no API key or if there's an error
      return (
        <a
          href={url}
          target='_blank'
          rel='noopener noreferrer'
          className='text-blue-600 hover:text-blue-800 underline flex items-center gap-2'
        >
          {url}
        </a>
      )
    }
  }

  function displayFile(url: string) {
    const extension = getFileExtension(url)
    switch (extension) {
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
      case 'bmp':
      case 'svg':
        return (
          <div className='d-flex flex-row gap-2'>
            <img src={url} alt='file' className='w-100 h-100' />
          </div>
        )
      case 'pdf':
      case 'csv':
      case 'txt':
      case 'doc':
      case 'docx':
      case 'xls':
      case 'xlsx':
      case 'ppt':
      case 'pptx':
      case 'zip':
        return (
          <>
            <div className='d-flex flex-row' style={{ width: 'fit-content' }}>
              <i className='bi bi-paperclip pt-1 me-2 text-black fw-bold fs-5'></i>
              <p>{getFileNameWithExtension(url)}</p>
            </div>
          </>
        )
      default:
        return <p>Unsupported file type.</p>
    }
  }

  function isValidFileType(url: string) {
    if (!url) return false
    const validExtensions = [
      'jpg',
      'jpeg',
      'png',
      'gif',
      'bmp',
      'svg',
      'csv',
      'pdf',
      'doc',
      'docx',
      'xls',
      'xlsx',
      'ppt',
      'pptx',
      'txt',
      'zip'
    ]
    return validExtensions.includes(getFileExtension(url))
  }

  let renderedMessage

  if (message && isMapUrl(message)) {
    renderedMessage = (
      <div
        className='px-4 pt-4 pb-4 mb-0 rounded text-dark fw-bold mw-lg-400px text-start'
        style={{ boxShadow: 'rgba(0, 0, 0, 0.16) 0px 2px 8px 0px' }}
      >
        {displayMap(message)}
      </div>
    )
  } else if (message && isValidFileType(message)) {
    renderedMessage = (
      <p
        className='px-4 pt-4 pb-0 mb-0 rounded text-dark fw-bold mw-lg-400px text-start'
        style={{ boxShadow: 'rgba(0, 0, 0, 0.16) 0px 2px 8px 0px' }}
      >
        {displayFile(message)}
      </p>
    )
  } else {
    const bgColor = side === 'rhs' ? 'primary' : 'info'
    const textAlign = side === 'center' ? 'center' : 'start'

    renderedMessage = (
      <p
        dangerouslySetInnerHTML={{ __html: message }}
        className={`p-5 mb-0 rounded bg-light-${bgColor} text-dark fw-bold mw-lg-400px text-${textAlign}`}
      />
    )
  }

  return (
    <>
      {renderedMessage}
      {side === 'lhs' && <i className='bi bi-check-all text-primary'></i>}
      {side === 'dash-user' && !!readByRecipientId && readByRecipientId > 0 && (
        <i className='bi bi-check-all'></i>
      )}
    </>
  )
}

export default TextTemplate
